/**
 * OLVASHATÓ KIEMELŐ SZÍNEK — közös forrás a komponenseknek ÉS a kontraszt-őr tesztnek
 * [Web 7 / T2, 2026-10-06].
 *
 * ══ MI VOLT A BAJ (lelet: WEB_UX_2KOR (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 4.1, 1.5, 2.4) ══
 *  · 4.1 — A kód `success.lighter` / `info.lighter` / `warning.lighter` / `error.lighter`
 *    palettakulcsokra hivatkozott. Ilyen kulcs a MUI 7.3.2 palettájában NINCS, és a saját
 *    témáink (`lightTheme.ts`, `darkTheme.ts`) sem definiálják → az `sx` a nyers
 *    `"success.lighter"` szöveget írta CSS-színnek, amit a böngésző eldob: a jelenléti
 *    kártya „Jelen / Pótlás / Fizetett" kiemelése és az „Elutasítás oka" panel háttere
 *    SOSEM jelent meg.
 *  · 1.5 — A kifizetés-összesítő `primary.light` hátterén a `primary.contrastText`
 *    világosban 3,31:1, sötétben (a Paper fehér rétegével) 2,77:1 volt (WCAG AA: 4,5:1).
 *  · 2.4 — Az „N mentetlen változás" számláló `warning.main` színe fehér papíron 3,12:1.
 *  · Mérés közben derült ki: a jelenlévő kártya `success.light` háttere SÖTÉT témában
 *    (#7bc67e) a világos szöveggel 1,83:1 — a kártya teljes szövege olvashatatlan volt;
 *    a böngészős E2E pedig a név-linket (sötétben 2,09:1) és a sötét téma `body1` saját
 *    színét az összesítőn (4,10:1) fogta meg.
 *
 * ══ A MEGOLDÁS ══
 * NEM vezetünk be új palettakulcsot (`lighter`) — ehhez `declare module` bővítés kellene
 * mindkét témában, és a kulcs neve megint csak egy szöveg lenne, amit semmi nem ellenőriz.
 * Helyette a kiemelés a téma `main` színéből számolt, a felület (papír) színére előre
 * RÁKEVERT, átlátszatlan szín; a szövegszínt pedig addig sötétítjük (világos témában) /
 * világosítjuk (sötét témában), amíg MINDEN háttéren, amelyen megjelenik, el nem éri a
 * 4,5:1-et. Ugyanezek a függvények adják a komponens `sx`-ét és a teszt mérését, így a
 * kontraszt-őr a TÉNYLEGESEN kirajzolt színpárt méri (nem egy másolatot).
 *
 * ⚠️ SZÁNDÉKOSAN NYITOTT KIVÉTEL [Web 7 / T2 javító kör 2, 2026-10-06 — M-D]: a „minden
 * háttéren ≥ 4,5:1" a kártya SAJÁT, átlátszatlan hátterére igaz. A HIÁNYZÓ (nem jelen lévő)
 * játékos kártyáját az `EventAttendance` egészében halványítja (`opacity: 0.7`), így ott a
 * ténylegesen látott szöveg a lap hátterével keveredik — a 2. validátor-kör mérése szerint
 * világos témában a név-link 3,83:1, a „Fizetett" 3,65:1, a `text.secondary` 3,03:1. A halványítás
 * dizájn-döntés (a hiányzó sor hátrébb lépjen), NEM a T2 tárgya; ezt a recept nem kezeli, és a
 * kontraszt-őr sem méri. Ha módosítani kell, az klub-/dizájn-döntés (pl. opacity helyett szürke
 * szöveg) — addig nyitott tétel.
 *
 * Őrök: `src/theme/__tests__/readableColors.contrast.test.ts` (kontraszt, mindkét téma),
 * `src/theme/__tests__/paletteKeys.guard.test.ts` (nem létező palettakulcs a forrásban).
 */
import {
  Theme,
  alpha,
  darken,
  lighten,
  decomposeColor,
  getContrastRatio,
  getOverlayAlpha,
} from '@mui/material/styles';

export type Tone = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';

/** WCAG 2.x AA, normál méretű szöveg. */
export const MIN_TEXT_CONTRAST = 4.5;
/** Biztonsági ráhagyás a kerekítésekre (böngésző-oldali 8 bites kerekítés). */
const CONTRAST_MARGIN = 0.1;

/** Az árnyalás erőssége (a `main` szín átlátszósága a papír fölött). */
export const TINT = {
  /** A jelenlévő játékos kártyájának halvány háttere. */
  card: 0.12,
  /** A bejelölt „Jelen / Pótlás / Fizetett" címke kiemelése (a kártyán legyen elkülönülő). */
  mark: 0.28,
  /** Panel-háttér (elutasítás oka, edzőnkénti beszedés sora). */
  panel: 0.14,
} as const;

const toRgb = (r: number, g: number, b: number) =>
  `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;

/**
 * Átlátszó (rgba) színt átlátszatlanra kever a megadott (átlátszatlan) alapon — így a
 * kontraszt pontosan számolható, és a kirajzolt szín nem függ attól, mi van alatta.
 */
export function flatten(color: string, base: string): string {
  const fg = decomposeColor(color);
  const bg = decomposeColor(base);
  if (fg.type === 'color' || bg.type === 'color') {
    throw new Error(`flatten: nem támogatott színformátum (${color} / ${base})`);
  }
  const a = fg.values.length > 3 ? Number(fg.values[3]) : 1;
  const ch = (i: number) => Number(fg.values[i]) * a + Number(bg.values[i]) * (1 - a);
  return toRgb(ch(0), ch(1), ch(2));
}

/** Kontraszt (átlátszó előtérszínt előbb a háttérre keverjük). */
export function contrastOn(fg: string, bg: string): number {
  return getContrastRatio(flatten(fg, bg), bg);
}

/**
 * A `Paper` TÉNYLEGES felszíne: sötét témában a MUI `elevation`-nál fehér réteget
 * (`--Paper-overlay`) kever a `background.paper`-re; világosban nincs réteg.
 * Az `outlined` változat és a sima `Box` → `elevation = 0`.
 */
export function surfaceColor(theme: Theme, elevation = 0): string {
  const paper = flatten(theme.palette.background.paper, '#000');
  if (theme.palette.mode !== 'dark' || elevation <= 0) return paper;
  return flatten(alpha('#fff', getOverlayAlpha(elevation)), paper);
}

/** A tónus `main` színe `strength` erősséggel a felszínre keverve — ÁTLÁTSZATLAN. */
export function tintSurface(theme: Theme, tone: Tone, strength: number, base = surfaceColor(theme)): string {
  return flatten(alpha(theme.palette[tone].main, strength), base);
}

/**
 * A tónus OLVASHATÓ szövegszíne a megadott háttereken: világos témában a `dark`
 * árnyalatból indul és addig sötétít, sötét témában a `light`-ból indul és addig
 * világosít, amíg MINDEN háttéren ≥ 4,5:1 (+ráhagyás) nem lesz. (A megadott, átlátszatlan
 * háttereken — a halványított hiányzó kártyára lásd a fájl eleji M-D kivételt.)
 */
export function readableTone(theme: Theme, tone: Tone, backgrounds: string[]): string {
  const isDark = theme.palette.mode === 'dark';
  const start = isDark ? theme.palette[tone].light : theme.palette[tone].dark;
  const ok = (c: string) =>
    backgrounds.every(bg => contrastOn(c, bg) >= MIN_TEXT_CONTRAST + CONTRAST_MARGIN);
  for (let step = 0; step <= 20; step++) {
    const amount = step * 0.05;
    const candidate = flatten(isDark ? lighten(start, amount) : darken(start, amount), '#000');
    if (ok(candidate)) return candidate;
  }
  // 20 lépés után a szín fekete/fehér — ez minden értelmes háttéren olvasható.
  return isDark ? '#ffffff' : '#000000';
}

/* ─────────────── Képernyő-receptek (a komponensek EZEKET használják) ─────────────── */

export type AttendanceMarkTone = 'success' | 'info' | 'warning';

/** Jelenléti kártya háttere (`EventAttendance`, telefonos/tabletes kártyás ág). */
export function attendanceCardBg(theme: Theme, isPresent: boolean): string {
  return isPresent ? tintSurface(theme, 'success', TINT.card) : surfaceColor(theme);
}

/** A bejelölt „Jelen" (success) / „Pótlás" (info) / „Fizetett" (warning) címke háttere. */
export function attendanceMarkBg(theme: Theme, tone: AttendanceMarkTone): string {
  return tintSurface(theme, tone, TINT.mark);
}

/** Minden háttér, amelyen a jelenléti kártya szövege megjelenhet. */
export function attendanceCardBackgrounds(theme: Theme): string[] {
  return [
    attendanceCardBg(theme, false),
    attendanceCardBg(theme, true),
    attendanceMarkBg(theme, 'success'),
    attendanceMarkBg(theme, 'info'),
    attendanceMarkBg(theme, 'warning'),
  ];
}

/**
 * Az ASZTALI (≥ 900 px) jelenléti tábla `Paper`-jének emelése (`renderDesktopTable`).
 * A komponens EZT a konstanst adja át `elevation`-nek, így a szövegszín ugyanarra a
 * felszínre van számolva, amelyen sötét témában a MUI fehér rétege ténylegesen megjelenik.
 */
export const ATTENDANCE_TABLE_ELEVATION = 1;

/**
 * Minden háttér, amelyen a jelenléti ív SZÍNES címkéje megjelenhet:
 * a kártyás ág összes háttere + az asztali tábla felszíne (emelt `Paper`) + a
 * „Hogyan olvasd" jelmagyarázat felszíne (`outlined` `Paper` → emelés nélkül).
 *
 * [Web 7 / T2 javító kör 1, 2026-10-06 — F1] Az első körben a szín CSAK a kártyás ág
 * hátterein volt mérve; az asztali tábla „Fizetett" (`warning.dark`, világosban 3,79:1),
 * „Beszedett" (`success.dark`, sötétben a `Paper` rétegével 2,81:1) és „Beszedendő"
 * (`primary.main`, sötétben 2,18:1) címkéje a régi színen maradt, a jelmagyarázat pedig
 * már az új színt mutatta — a kettő eltért. Most EGY szín szolgálja ki mindhárom helyet.
 */
export function attendanceToneBackgrounds(theme: Theme): string[] {
  return [
    ...attendanceCardBackgrounds(theme),
    surfaceColor(theme, ATTENDANCE_TABLE_ELEVATION),
    surfaceColor(theme),
  ];
}

/**
 * A színes címkék („Fizetett" = warning, „Beszedett" = success, „Beszedendő" és a név-link =
 * primary) szövegszíne — a kártyán, az asztali táblában ÉS a „Hogyan olvasd" jelmagyarázatban
 * UGYANAZ, és mindegyik háttéren olvasható (`attendanceToneBackgrounds`).
 */
export function attendanceToneText(theme: Theme, tone: Tone): string {
  return readableTone(theme, tone, attendanceToneBackgrounds(theme));
}

/**
 * „Elutasítás oka" panel (`EventDetailsDialog`, `PendingReservations`).
 * `field`: a panelbe ágyazott szövegmező háttere (a beírt szöveg `text.primary`) —
 * [Web 7 / T2 javító kör 1, 2026-10-06 — F3] a receptben, hogy a komponens-teszt a
 * szövegmező számított hátterét is a közös forráshoz mérhesse.
 */
export function rejectionPanelColors(theme: Theme): { bg: string; title: string; field: string } {
  const bg = tintSurface(theme, 'error', TINT.panel);
  return { bg, title: readableTone(theme, 'error', [bg]), field: surfaceColor(theme) };
}

/** Edzőnkénti beszedés sora a jelenléti ív alatt (`EventAttendance`). */
export function collectionRowColors(theme: Theme): { bg: string; amount: string } {
  const bg = tintSurface(theme, 'success', TINT.panel);
  return { bg, amount: readableTone(theme, 'success', [bg]) };
}

/**
 * Kifizetés-összesítő (`CoachPaymentManagement`): `primary.main` háttér +
 * `primary.contrastText` szöveg. A `Paper` alapértelmezett `elevation = 1`, ezért sötét
 * témában a MUI fehér rétege is rákerül — `surface` ezt a TÉNYLEGESEN látott színt adja.
 */
export const PAYMENT_SUMMARY_ELEVATION = 1;
export function paymentSummaryColors(theme: Theme): { bg: string; text: string; surface: string } {
  const bg = theme.palette.primary.main;
  const surface =
    theme.palette.mode === 'dark'
      ? flatten(alpha('#fff', getOverlayAlpha(PAYMENT_SUMMARY_ELEVATION)), flatten(bg, '#000'))
      : flatten(bg, '#000');
  return { bg, text: theme.palette.primary.contrastText, surface };
}

/** A rögzített mentés-sáv `Paper`-jének emelése (`EventAttendance`). */
export const SAVE_BAR_ELEVATION = 8;
/** „N mentetlen változás" számláló színe a mentés-sávon. */
export function unsavedCounterColor(theme: Theme): string {
  return readableTone(theme, 'warning', [surfaceColor(theme, SAVE_BAR_ELEVATION)]);
}
